import { Box } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { HOLIDAY_GROUPS } from "../../../theme/tokens";
import { DateInput } from "../../UIToolkit/Inputs/DateInput";
import { InlineHint } from "../../UIToolkit/molecules/InlineHint";
import SystemDialog from "../../UIToolkit/Popups/Dialog";
import { HolidayGroupSelect } from "./HolidayGroupSelect";
import { HolidayNameField } from "./HolidayNameField";
import { HolidayShiftFields } from "./HolidayShiftFields";
import type { Holiday, HolidayFirstPart, HolidayGroup, HolidayLastPart } from "./holidayApi";
import { holidayNameLooksLikeDate } from "./holidayFormat";
import { useDepartmentToday, useEditHoliday } from "./holidayQueries";

type Props = { holiday: Holiday; groups: readonly HolidayGroup[]; onClose: () => void };

const ISO_DATE = "yyyy-MM-dd";

/** "Edit holiday": name, date, the group that works it, and its first and last shift. Only before it's applied. */
export const HolidayEditDialog: FC<Props> = ({ holiday, groups, onClose }) => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const today = useDepartmentToday();
	const [name, setName] = useState(holiday.name);
	const [date, setDate] = useState<DateTime>(holiday.date);
	const [groupId, setGroupId] = useState<string | null>(holiday.selectedGroupId);
	const [firstPart, setFirstPart] = useState<HolidayFirstPart>(holiday.firstPart);
	const [lastPart, setLastPart] = useState<HolidayLastPart>(holiday.lastPart);
	const [missingName, setMissingName] = useState(false);
	const edit = useEditHoliday();
	const nameLooksLikeDate = holidayNameLooksLikeDate(name);

	const handleConfirm = () => {
		if (nameLooksLikeDate) return;
		if (name.trim() === "") {
			setMissingName(true);
			return;
		}
		edit.mutate(
			// The group is always sent — the server needs it on every edit, unchanged or not.
			{ holidayId: holiday.id, body: { name: name.trim(), date: date.toFormat(ISO_DATE), firstPart, lastPart, selectedGroupId: groupId } },
			{
				onSuccess: (saved) => {
					showSnackbar(t("holidays.dialog.saved", { name: saved.name }), "success");
					onClose();
				},
			},
		);
	};

	const body = (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1, width: { sm: HOLIDAY_GROUPS.FORM_DIALOG_BODY_WIDTH_PX } }}>
			<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
				<HolidayNameField
					value={name}
					onChange={(next) => {
						setName(next);
						setMissingName(false);
					}}
					dataTestId="holiday-dialog-name"
				/>
				<DateInput
					label={t("date")}
					value={date}
					onChange={(next) => setDate(next.startOf("day"))}
					minDate={today}
					formControlSx={{ width: "100%" }}
					dataTestId="holiday-dialog-date"
				/>
			</Box>
			{missingName && (
				<InlineHint tone="warning" dataTestId="holiday-dialog-missing">
					{t("holidays.dialog.missingName")}
				</InlineHint>
			)}
			<HolidayGroupSelect
				groups={groups}
				value={groupId}
				onChange={setGroupId}
				allowNone={false}
				emptyLabel={t("holidays.group.choose")}
				variant="field"
				label={t("holidays.list.column.works")}
				showLabel={true}
				// Once the holiday's hours have started, the group that works it stays.
				disabled={!holiday.canChangeGroup}
				dataTestId="holiday-dialog-works"
			/>
			<HolidayShiftFields firstPart={firstPart} lastPart={lastPart} onFirstPartChange={setFirstPart} onLastPartChange={setLastPart} />
		</Box>
	);

	return (
		<SystemDialog
			open
			onClose={onClose}
			title={t("holidays.dialog.editTitle")}
			body={body}
			cancel={t("cancel")}
			confirm={t("save")}
			confirmButtonVariant="primary"
			onConfirm={handleConfirm}
			confirmDisabled={nameLooksLikeDate}
			confirmLoading={edit.isPending}
			dataTestId="holiday-edit-dialog"
			confirmDataTestId="holiday-edit-confirm"
			cancelDataTestId="holiday-edit-cancel"
		/>
	);
};
